/**
 * Country-specific configuration system
 *
 * Loads configuration files from countries/{country}/ directory based on NEXT_PUBLIC_COUNTRY
 * If a config file exists in the country directory, it overrides the default config
 */

export type CountryKey =
  | "greece"
  | "georgia"
  | "ukraine"
  | "france"
  | "spain"
  | "uae"
  | "stage";

/**
 * Get current country from environment variable
 */
export const getCurrentCountry = (): CountryKey => {
  const country = process.env.NEXT_PUBLIC_COUNTRY as CountryKey;
  return country || "greece"; // Default to Greece
};

type ConfigName =
  | "locale"
  | "currency"
  | "contacts"
  | "country"
  | "amenities"
  | "cacheLifetime"
  | "call"
  | "characteristics"
  | "formSteps"
  | "nav"
  | "navigation"
  | "nextSeo"
  | "objectType"
  | "offices"
  | "pages"
  | "residential"
  | "socials"
  | "write";

// ---------------------------------------------------------------------------
// Webpack contexts — gather all country overrides at build-time.
// They create static dependency graphs, so there will be no warnings.
// ---------------------------------------------------------------------------

// Minimal typings for webpack's require.context
interface IWebpackRequireContext {
  (id: string): unknown;

  keys(): string[];
}

// Configuration cache to prevent repeated loading
const configCache = new Map<string, any>();

// Track logged configurations to prevent spam
const loggedConfigs = new Set<string>();

// Track loaded configurations for summary logging
const loadedConfigs: Array<{
  name: string;
  country: string;
  hasOverride: boolean;
  timestamp: string;
}> = [];

// eslint-disable-next-line @typescript-eslint/no-var-requires
const ctxAll = (require as any).context(
  "./countries",
  true,
  /\.config\.(ts|js)$/,
) as IWebpackRequireContext;

/**
 * Merge default config with override version for current country.
 * Supports *any* `configName` (locale, currency, contacts, country, socials, etc.)
 * Just create file `src/config/countries/{country}/{configName}.config.ts`.
 */
export function createConfigLoader<T>(defaultConfig: T, configName: string): T {
  const country = getCurrentCountry();
  const cacheKey = `${country}-${configName}`;

  // Return cached version if it exists
  if (configCache.has(cacheKey)) {
    return configCache.get(cacheKey);
  }

  const modulePathTs = `./${country}/${configName}.config.ts`;
  const modulePathJs = `./${country}/${configName}.config.js`;
  let override: Partial<T> | undefined;

  if (ctxAll.keys().includes(modulePathTs)) {
    const mod = ctxAll(modulePathTs) as any;
    override = mod.default ?? mod[`${configName}Config`];
  } else if (ctxAll.keys().includes(modulePathJs)) {
    const mod = ctxAll(modulePathJs) as any;
    override = mod.default ?? mod[`${configName}Config`];
  }

  let result: T;
  const logKey = `${configName}-${country}`;
  
  if (override) {
    // Track loaded config for summary (only once per config)
    if (!loggedConfigs.has(logKey)) {
      loadedConfigs.push({
        name: configName,
        country,
        hasOverride: true,
        timestamp: new Date().toISOString(),
      });
      loggedConfigs.add(logKey);
    }
    // Handle arrays differently from objects
    if (Array.isArray(defaultConfig) && Array.isArray(override)) {
      result = override as T;
    } else {
      result = { ...defaultConfig, ...override } as T;
    }
  } else {
    // Track loaded config for summary (only once per config)
    if (!loggedConfigs.has(logKey)) {
      loadedConfigs.push({
        name: configName,
        country,
        hasOverride: false,
        timestamp: new Date().toISOString(),
      });
      loggedConfigs.add(logKey);
    }
    result = defaultConfig;
  }

  // Cache the result
  configCache.set(cacheKey, result);
  return result;
}

/**
 * Configuration info for logging
 */
export function getConfigInfo() {
  const country = getCurrentCountry();
  const env = process.env.NODE_ENV || "development";
  const locale = process.env.NEXT_PUBLIC_DEFAULT_LOCALE || "el";

  return {
    country,
    environment: env,
    defaultLocale: locale,
    timestamp: new Date().toISOString(),
  };
}

// Use global flag to survive HMR (Hot Module Replacement)
declare global {
  // eslint-disable-next-line no-var
  var __STARTUP_INFO_LOGGED: string | undefined;
  // eslint-disable-next-line no-var
  var __CONFIG_SUMMARY_LOGGED: string | undefined;
  // eslint-disable-next-line no-var
  var __STARTUP_SESSION_ID: string | undefined;
}



/**
 * Log configuration summary - shows all loaded configs at once
 */
export function logConfigSummary() {
  if (typeof window !== "undefined") return; // Only log on server
  if (loadedConfigs.length === 0) return; // No configs to log
  
  const sessionId = global.__STARTUP_SESSION_ID;


  const overrideConfigs = loadedConfigs.filter((c) => c.hasOverride);
  const defaultConfigs = loadedConfigs.filter((c) => !c.hasOverride);
  const country = getCurrentCountry();

  console.log(`\n📊 Configuration Summary for \x1b[36m${country.toUpperCase()}\x1b[0m:`);

  if (overrideConfigs.length > 0) {
    console.log(`\n🎯 \x1b[32mOverride Configs\x1b[0m (${overrideConfigs.length}):`);
    overrideConfigs.forEach((config) => {
      console.log(
        `   • \x1b[32m${config.name.toUpperCase()}\x1b[0m`,
      );
    });
  }

  if (defaultConfigs.length > 0) {
    console.log(`\n📋 \x1b[90mDefault Configs\x1b[0m (${defaultConfigs.length}):`);
    defaultConfigs.forEach((config) => {
      console.log(
        `   • \x1b[90m${config.name.toUpperCase()}\x1b[0m`,
      );
    });
  }

  console.log(
    `\n✅ Total: ${loadedConfigs.length} configurations loaded successfully\n`,
  );
}

/**
 * Beautiful startup logger - only logs once per session
 */
export function logStartupInfo() {
  if (typeof window !== "undefined") return; // Only log on server
  
  // const sessionId = global.__STARTUP_SESSION_ID;
  // if (global.__STARTUP_INFO_LOGGED === sessionId) return; // Already logged this session
  
  // global.__STARTUP_INFO_LOGGED = sessionId;

  const config = getConfigInfo();
  const isDev = config.environment === "development";

  console.log(
    `🌍 Country: \x1b[36m\x1b[1m${config.country.toUpperCase()}\x1b[0m`,
  );
  console.log(
    `🌐 Locale: \x1b[35m${config.defaultLocale.toUpperCase()}\x1b[0m`,
  );
  console.log(
    `🔧 Environment: \x1b[33m${config.environment.toUpperCase()}\x1b[0m`,
  );
  console.log(
    `⏰ Started: \x1b[90m${new Date(config.timestamp).toLocaleString()}\x1b[0m`,
  );

  if (isDev) {
    console.log(`🚀 Development mode enabled`);
  } else {
    console.log(`🏢 Production mode active`);
  }

  // Log configuration summary after a short delay to collect all configs
  setTimeout(() => {
    logConfigSummary();
  }, 1000);
}
